// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — RevenueTrendChart
// ══════════════════════════════════════════════════════════════════════════════

import {
  AreaChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card } from '@components/ui/Primitives';

export function RevenueTrendChart({ sales = [] }) {
  const { theme } = useTheme();
  const { t } = useLang();

  // Prepare chart data (reverse for chronological order)
  const chartData = [...sales]
    .slice(0, 30)
    .reverse()
    .reduce((acc, sale) => {
      const dateKey = sale.sale_date?.slice(5);
      const existing = acc.find(a => a.date === dateKey);
      if (existing) {
        existing.revenue += parseFloat(sale.total_revenue || 0);
        existing.profit += parseFloat(sale.total_revenue || 0) * 0.382;
      } else {
        acc.push({
          date: dateKey,
          revenue: parseFloat(sale.total_revenue || 0),
          profit: parseFloat(sale.total_revenue || 0) * 0.382
        });
      }
      return acc;
    }, []);

  const tooltipStyle = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px'
  };

  return (
    <Card style={{ padding: 22 }}>
      <h4 style={{
        margin: '0 0 16px',
        fontSize: 14,
        fontWeight: 700,
        color: theme.text
      }}>
        {t.revenueTrend}
      </h4>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.chartColors[0]} stopOpacity={0.35} />
              <stop offset="95%" stopColor={theme.chartColors[0]} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
          <XAxis
            dataKey="date"
            tick={{ fill: theme.textTertiary, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            interval={Math.ceil(chartData.length / 10)}
          />
          <YAxis
            tick={{ fill: theme.textTertiary, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip contentStyle={tooltipStyle} />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke={theme.chartColors[0]}
            fill="url(#revGradient)"
            strokeWidth={2}
            name={t.totalRevenue}
          />
          <Line
            type="monotone"
            dataKey="profit"
            stroke={theme.chartColors[2]}
            strokeWidth={2}
            dot={false}
            name={t.netProfit}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}
